import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of, delay, map } from 'rxjs';
import {
  JudgeCase,
  HearingCard,
  RulingCard,
  ProcedureBrief,
  MOCK_JUDGE_CASES,
  MOCK_JUDGE_METRICS,
  MOCK_HEARINGS,
  MOCK_RULINGS,
  MOCK_PROCEDURAL_BRIEF,
} from '../mock/judge.mock';
import { PageParams, PaginatedResponse } from '../models/pagination.model';
import { ApiResponse } from '../models/api-response.model';
import { PagedResult } from '../models/case.model';
import { environment } from '../../../environments/environment';

/** Shape of a single item from GET /api/Judge/cases */
export interface JudgeCaseItem {
  id: string;
  reference: string;
  parties: string;
  division: string;
  status: number;
  submittedAtUtc: string;
  hearingDateUtc?: string | null;
}

/** Raw document entry inside the documentInventory JSON string */
export interface BriefDocumentEntry {
  DocumentType: string;
  FileName: string;
  UploadedAtUtc: string;
}

/** Shape of GET /api/Judge/cases/{id}/brief */
export interface JudgeBriefItem {
  caseFilingId: string;
  reference: string;
  summary: string;
  parties: string;
  legalIssues: string;
  documentInventory: string; // JSON string → BriefDocumentEntry[]
  riskAssessment: string; // JSON string → object (currently {})
}

@Injectable({ providedIn: 'root' })
export class JudgeService {
  constructor(private http: HttpClient) {}

  getMetrics(): Observable<typeof MOCK_JUDGE_METRICS> {
    if (environment.useMock) return of(MOCK_JUDGE_METRICS).pipe(delay(100));
    return this.http.get<typeof MOCK_JUDGE_METRICS>(
      `${environment.apiUrl}/judge/metrics`,
    );
  }

  /** Paginated assigned cases — GET /api/Judge/cases?PageNumber=N&PageSize=N */
  getCases(
    pageNumber = 1,
    pageSize = 10,
    status?: number,
  ): Observable<PagedResult<JudgeCaseItem>> {
    let params = new HttpParams()
      .set('PageNumber', pageNumber)
      .set('PageSize', pageSize);
    if (status !== undefined) params = params.set('Status', status);
    return this.http
      .get<
        ApiResponse<PagedResult<JudgeCaseItem>>
      >(`${environment.apiUrl}/Judge/cases`, { params })
      .pipe(map((r) => r.data));
  }

  getHearings(): Observable<HearingCard[]> {
    if (environment.useMock) return of(MOCK_HEARINGS).pipe(delay(150));
    return this.http.get<HearingCard[]>(`${environment.apiUrl}/judge/hearings`);
  }

  getPendingRulings(
    pageNumber = 1,
    pageSize = 10,
  ): Observable<PagedResult<JudgeCaseItem>> {
    const params = new HttpParams()
      .set('PageNumber', pageNumber)
      .set('PageSize', pageSize);
    return this.http
      .get<ApiResponse<PagedResult<JudgeCaseItem>>>(
        `${environment.apiUrl}/Judge/pending-rulings`,
        { params },
      )
      .pipe(map((r) => r.data));
  }

  getRulings(): Observable<RulingCard[]> {
    if (environment.useMock) return of(MOCK_RULINGS).pipe(delay(150));
    return this.http.get<RulingCard[]>(`${environment.apiUrl}/judge/rulings`);
  }

  getBrief(caseId: string): Observable<JudgeBriefItem> {
    return this.http
      .get<
        ApiResponse<JudgeBriefItem>
      >(`${environment.apiUrl}/Judge/brief/${caseId}`)
      .pipe(map((r) => r.data));
  }

  // ── Actions (live endpoints) ──────────────────────────

  /** POST /api/Judge/schedule { caseFilingId, dateUtc, courtroom, type } */
  scheduleHearing(
    caseFilingId: string,
    dateUtc: string,
    courtroom: string,
    type: string,
  ): Observable<ApiResponse<unknown>> {
    return this.http.post<ApiResponse<unknown>>(
      `${environment.apiUrl}/Judge/schedule`,
      {
        caseFilingId,
        dateUtc,
        courtroom,
        type,
      },
    );
  }

  /** POST /api/Judge/private-note { caseFilingId, note } */
  savePrivateNote(
    caseFilingId: string,
    note: string,
  ): Observable<ApiResponse<unknown>> {
    return this.http.post<ApiResponse<unknown>>(
      `${environment.apiUrl}/Judge/private-note`,
      { caseFilingId, note },
    );
  }

  /** POST /api/Judge/request-docs { caseFilingId, note } */
  requestDocuments(
    caseFilingId: string,
    note: string,
  ): Observable<ApiResponse<unknown>> {
    return this.http.post<ApiResponse<unknown>>(
      `${environment.apiUrl}/Judge/request-docs`,
      { caseFilingId, note },
    );
  }

  /** POST /api/Judge/upload-judgment (multipart) { CaseFilingId, Summary, AwardAmount, File } */
  uploadJudgment(
    caseFilingId: string,
    file: File,
    summary: string,
    awardAmount?: number,
  ): Observable<ApiResponse<unknown>> {
    const form = new FormData();
    form.append('CaseFilingId', caseFilingId);
    form.append('Summary', summary);
    if (awardAmount !== undefined)
      form.append('AwardAmount', String(awardAmount));
    form.append('File', file);
    return this.http.post<ApiResponse<unknown>>(
      `${environment.apiUrl}/Judge/upload-judgment`,
      form,
    );
  }

  /**
   * POST /api/Judge/adjourn { hearingId, newDateUtc, reason }
   * Blocked: needs a hearingId we don't have yet — stays mock until the
   * cases/brief response surfaces hearing IDs.
   */
  adjourn(_ref: string, _data: object): Observable<{ success: boolean }> {
    return of({ success: true }).pipe(delay(600));
  }
}
